import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { getPrimaryImageUrl, getFallbackImageUrl } from '../../../utils/imageUtils';

const ProductsBlock = ({ data, isSelected, onSelect, onUpdate, isPreview }) => {
  const { themeStyles } = React.useContext(React.createContext({}));
  const products = useSelector(state => state.store?.products || []);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(data);

  const handleSave = () => {
    onUpdate(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(data);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Get unique categories
  const categories = ['all', ...new Set(products?.map(product => product.category).filter(Boolean))];

  // Filter products based on settings
  const getFilteredProducts = () => {
    let filtered = products?.filter(product => product.status === 'active') || [];
    
    if (editData.category && editData.category !== 'all') {
      filtered = filtered.filter(product => product.category === editData.category);
    }
    
    // Sort products
    switch (editData.sortBy) {
      case 'featured':
        filtered = filtered.filter(product => product.featured);
        break;
      case 'newest':
        filtered = filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'price-low':
        filtered = filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered = filtered.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        filtered = filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        // No sorting applied
        break;
    }
    
    return filtered.slice(0, editData.productCount || 6);
  };

  if (isPreview) {
    const displayProducts = getFilteredProducts();
    
    return (
      <section style={{
        padding: '4rem 0',
        backgroundColor: themeStyles?.background || '#ffffff'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: themeStyles?.text || '#333'
            }}>
              {data.title || 'Featured Products'}
            </h2>
            <p style={{
              fontSize: '1.125rem',
              color: themeStyles?.textLight || '#666',
              lineHeight: '1.6'
            }}>
              {data.description || 'Check out our best-selling products'}
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '2rem'
          }}>
            {displayProducts.map((product) => (
              <div
                key={product._id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-4px)';
                  e.target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
              >
                {/* Product Image */}
                <div style={{
                  height: '200px',
                  backgroundColor: '#f8f9fa',
                  backgroundImage: getPrimaryImageUrl(product) ? `url(${getPrimaryImageUrl(product)})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative'
                }}>
                  {product.featured && (
                    <div style={{
                      position: 'absolute',
                      top: '0.5rem',
                      left: '0.5rem',
                      backgroundColor: themeStyles?.accent || '#f59e0b',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      Featured
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div style={{ padding: '1.5rem' }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    marginBottom: '0.5rem',
                    color: themeStyles?.text || '#333',
                    lineHeight: '1.3'
                  }}>
                    {product.name}
                  </h3>
                  
                  {data.showDescription && (
                    <p style={{
                      color: themeStyles?.textLight || '#666',
                      marginBottom: '1rem',
                      fontSize: '0.875rem',
                      lineHeight: '1.5',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {product.shortDescription || product.description?.substring(0, 100) + '...'}
                    </p>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}>
                    {data.showPrice && (
                      <div>
                        <span style={{
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          color: themeStyles?.primary || '#007bff'
                        }}>
                          ${product.price}
                        </span>
                        {product.compareAtPrice && product.compareAtPrice > product.price && (
                          <span style={{
                            fontSize: '1rem',
                            color: themeStyles?.textLight || '#666',
                            textDecoration: 'line-through',
                            marginLeft: '0.5rem'
                          }}>
                            ${product.compareAtPrice}
                          </span>
                        )}
                      </div>
                    )}
                    <span style={{
                      fontSize: '0.875rem',
                      color: themeStyles?.textLight || '#666',
                      textTransform: 'capitalize'
                    }}>
                      {product.category}
                    </span>
                  </div>

                  <button
                    style={{
                      width: '100%',
                      backgroundColor: themeStyles?.primary || '#007bff',
                      color: 'white',
                      padding: '0.75rem',
                      borderRadius: '0.25rem',
                      border: 'none',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = themeStyles?.secondary || '#0056b3';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = themeStyles?.primary || '#007bff';
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>

          {displayProducts.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: themeStyles?.textLight || '#666'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🛍️</div>
              <p>No products found with current settings.</p>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <div
      style={{
        border: isSelected ? '2px solid #007bff' : '2px solid #dee2e6',
        borderRadius: '0.5rem',
        backgroundColor: 'white',
        marginBottom: '1rem',
        position: 'relative'
      }}
      onClick={onSelect}
    >
      {/* Block Header */}
      <div style={{
        padding: '0.75rem 1rem',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #dee2e6',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>🛍️</span>
          <span style={{ fontWeight: '600' }}>Products Display</span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(!isEditing);
          }}
          style={{
            padding: '0.25rem 0.5rem',
            backgroundColor: isEditing ? '#dc3545' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            fontSize: '0.75rem'
          }}
        >
          {isEditing ? 'Cancel' : 'Edit'}
        </button>
      </div>

      {/* Block Content */}
      <div style={{ padding: '1rem' }}>
        {isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Title */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Section Title
              </label>
              <input
                type="text"
                value={editData.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Description */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Description
              </label>
              <textarea
                value={editData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows="2"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Product Count */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Number of Products to Show
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={editData.productCount || 6}
                onChange={(e) => handleInputChange('productCount', parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Category Filter */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Category Filter
              </label>
              <select
                value={editData.category || 'all'}
                onChange={(e) => handleInputChange('category', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                <option value="all">All Categories</option>
                {categories.slice(1).map(category => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Sort By
              </label>
              <select
                value={editData.sortBy || 'featured'}
                onChange={(e) => handleInputChange('sortBy', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                <option value="featured">Featured Products</option>
                <option value="newest">Newest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name">Name A-Z</option>
              </select>
            </div>

            {/* Display Options */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Display Options
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={editData.showPrice || false}
                    onChange={(e) => handleInputChange('showPrice', e.target.checked)}
                    style={{ transform: 'scale(1.2)' }}
                  />
                  Show Price
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={editData.showDescription || false}
                    onChange={(e) => handleInputChange('showDescription', e.target.checked)}
                    style={{ transform: 'scale(1.2)' }}
                  />
                  Show Description
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                onClick={handleSave}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '0.25rem',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: '600' }}>
              {data.title || 'Products Display'}
            </h3>
            <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>
              {data.productCount || 6} products • {data.category || 'all'} category
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsBlock;
